import { SEO } from "@/components/SEO";
import { Link, useRoute } from "wouter";

const POSTS_DATA: Record<string, { title: string; content: React.ReactNode; date: string }> = {
    "what-is-ragi-drink": {
        title: "What Is a Ragi Drink?",
        date: "June 18, 2026",
        content: (
            <>
                <p className="mb-4">
                    A ragi drink is a beverage made from ragi, also known as finger millet. In many Indian homes, ragi has been used in porridges, malt drinks, mudde, and ganji because it is filling, earthy, and deeply familiar.
                </p>
                <p className="mb-4">
                    Ganji Drinks takes that traditional idea and makes it ready to drink. Instead of preparing ragi at home every morning, you can pick up a 250ml bottle and still get the comfort of a desi millet drink.
                </p>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Why people search for ragi drinks</h3>
                <p className="mb-4">
                    People often look for ragi drinks when they want healthy drinks, millet beverages, traditional Indian drinks, or alternatives to soda and sugary packaged drinks. Ragi is valued for calcium, iron, and fiber, which makes it a strong fit for everyday routines.
                </p>
                <p>
                    Ganji Drinks is available as Sweet Ganji with jaggery and cardamom, and Salt Ganji for people who prefer a simpler traditional taste.
                </p>
            </>
        )
    },
    "healthy-drinks-india": {
        title: "Healthy Drinks in India: Why Millet Beverages Matter",
        date: "June 18, 2026",
        content: (
            <>
                <p className="mb-4">
                    The Indian drinks market is full of colas, fizzy drinks, packaged juices, and energy drinks. They are convenient, but they do not always feel connected to how Indian families have traditionally eaten and lived.
                </p>
                <p className="mb-4">
                    Millet beverages offer another direction. Ragi, jowar, bajra, and other millets have long been part of Indian food culture. A ragi-based drink gives people a familiar, filling option that still works for modern school, office, and travel schedules.
                </p>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Where Ganji Drinks fits</h3>
                <p className="mb-4">
                    Ganji Drinks is built for people who search for healthy drinks, ragi drinks, millet drinks, and desi energy drinks but still want something easy to carry. The goal is not to make tradition feel old. The goal is to make it usable again.
                </p>
                <p>
                    For Bengaluru customers, Ganji Drinks brings ready-to-drink ragi beverages into a format that can compete with everyday packaged drinks while staying rooted in Indian taste.
                </p>
            </>
        )
    },
    "sweet-vs-salt-ganji": {
        title: "Sweet Ganji vs Salt Ganji",
        date: "June 18, 2026",
        content: (
            <>
                <p className="mb-4">
                    Ganji Drinks currently focuses on two simple ragi beverage styles: Sweet Ganji and Salt Ganji. Both are based on ragi, but they serve different moods.
                </p>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Sweet Ganji</h3>
                <p className="mb-4">
                    Sweet Ganji is made for people who like a softer, comforting drink. Jaggery and cardamom give it the familiar taste of a traditional Indian kitchen without making it feel like a regular soda.
                </p>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Salt Ganji</h3>
                <p className="mb-4">
                    Salt Ganji is for people who prefer a more direct, village-style ragi drink. It is simple, earthy, and closer to the everyday ganji many people remember.
                </p>
                <p>
                    If you are trying Ganji Drinks for the first time, start with Sweet Ganji if you want something familiar and easy. Choose Salt Ganji if you already enjoy traditional ragi flavors.
                </p>
            </>
        )
    },
    "product-testing": {
        title: "The Journey of Product Testing",
        date: "January 5, 2026",
        content: (
            <>
                <p className="mb-4">
                    At Ganjidrinks, we believe that you can't rush perfection. Before our bottles ever reached the shelves, they went through months of rigorous testing, tasting, and refining.
                </p>
                <p className="mb-4">
                    Our team traveled across Karnataka to source the finest finger millet (ragi). We worked with local farmers to understand the traditional ways of processing this supergrain to retain its nutritional value.
                </p>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Listening to Feedback</h3>
                <p className="mb-4">
                    We conducted blind taste tests with over 500 people. From college students to grandmothers, everyone had a say. Some said "more jaggery," others said "thicker texture." We listened to every single comment. This iterative process helped us nail the authentic taste that reminds you of home.
                </p>
                <p>
                    Today, when you open a bottle of Ganji, you aren't just drinking a beverage; you are experiencing a product built on community feedback and rigorous quality standards.
                </p>
            </>
        )
    },
    "we-are-hiring": {
        title: "We Are Hiring",
        date: "January 8, 2026",
        content: (
            <>
                <p className="mb-4">
                    Ganjidrinks is growing fast, and we are looking for passionate individuals to join our mission of reviving Indian heritage beverages.
                </p>
                <p className="mb-4">
                    We are currently hiring for roles in:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li><strong>Sales & Distribution:</strong> Help us reach more stores and customers.</li>
                    <li><strong>Digital Marketing:</strong> Tell our story to the world.</li>
                    <li><strong>Operations Interns:</strong> Learn the ropes of building a startup from scratch.</li>
                </ul>
                <h3 className="text-2xl mt-8 mb-4 font-bold" style={{ fontFamily: "'Patrick Hand', cursive" }}>Why Join Us?</h3>
                <p className="mb-4">
                    We are a team that values ownership, clarity, and action. If you want to work in an environment where your work has a direct impact, Ganjidrinks is the place for you.
                </p>
                <p>
                    Check out our LinkedIn page for detailed job descriptions or email your resume to <strong>ganjidrinks@gmail.com</strong>.
                </p>
            </>
        )
    }
};

export default function BlogPost() {
    const [match, params] = useRoute("/blog/:slug");
    const slug = params?.slug;
    const post = slug ? POSTS_DATA[slug] : null;

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl mb-4">Post not found</h1>
                    <Link href="/blog"><a className="underline">Back to Blog</a></Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground pb-12">
            <SEO
                title={`${post.title} | Ganji Drinks Ragi Beverage Blog`}
                description={`Read ${post.title} from Ganji Drinks, covering ragi drinks, healthy Indian beverages, millet nutrition, and traditional ganji culture.`}
                keywords={`${post.title}, ragi drink, ganji, healthy drinks India, millet beverage, traditional Indian drink, Ganji Drinks`}
                type="article"
                url={`https://ganjidrinks.shop/blog/${slug}`}
            />

            {/* Empty Header / Spacer */}
            <nav className="p-4 md:p-6 h-20 pointer-events-none sticky top-0 z-40 bg-transparent"></nav>

            <main className="container mx-auto px-4 pt-8 md:pt-12 max-w-3xl">
                {/* Back Button */}
                <div className="mb-8">
                    <Link href="/blog">
                        <a className="inline-flex items-center gap-2 hover:opacity-75 transition-opacity"
                            style={{ color: 'hsl(var(--ganji-rustic-brown))' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            <span className="text-lg" style={{ fontFamily: "'Patrick Hand', cursive" }}>Back to Blog</span>
                        </a>
                    </Link>
                </div>

                <article>
                    <header className="mb-8 text-center">
                        <span className="text-sm text-muted-foreground mb-3 block">{post.date}</span>
                        <h1
                            className="text-3xl md:text-5xl mb-4 leading-tight"
                            style={{ fontFamily: "'Permanent Marker', cursive", color: 'hsl(var(--ganji-rustic-brown))' }}
                        >
                            {post.title}
                        </h1>
                    </header>

                    <div
                        className="prose prose-lg mx-auto"
                        style={{
                            fontFamily: "'Open Sans', sans-serif",
                            color: 'hsl(var(--ganji-rustic-brown))'
                        }}
                    >
                        {post.content}
                    </div>
                </article>

                <div className="mt-16 pt-8 border-t border-border text-center">
                    <Link href="/blog">
                        <a className="inline-flex items-center gap-2 hover:underline opacity-70 hover:opacity-100">
                            ← Back to Ganji Stories
                        </a>
                    </Link>
                </div>
            </main>
        </div>
    );
}
